import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Twitter, Mail, Phone, MapPin } from "lucide-react";
import { LOGO_FULL, ORG } from "../lib/site";

const QUICK_LINKS = [
    { to: "/about", label: "About" },
    { to: "/framework", label: "Framework" },
    { to: "/what-we-do", label: "What We Do" },
    { to: "/research", label: "Research" },
    { to: "/for-hotels", label: "For Hotels" },
    { to: "/sustainability-check", label: "Free Check" },
    { to: "/partners", label: "Partners" },
    { to: "/get-involved", label: "Get Involved" },
    { to: "/contact", label: "Contact" },
    { to: "/hotels", label: "Member Hotels" },
    { to: "/hotel/login", label: "Hotel Login" },
];

const SOCIALS = [
    { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/in/global-green-hotels-ggh-624b5443a" },
    { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/share/199vu3yEPG/" },
    { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/global_green_hotel_ggh" },
    { icon: Twitter, label: "X (Twitter)", href: "https://x.com/greenglobalhote" },
];

export const Footer = () => (
    <footer data-testid="footer" className="grain bg-[#04150D] text-[#FDFBF7]">
        <div className="relative z-[2] mx-auto max-w-[1440px] px-5 pb-10 pt-20 sm:px-8 lg:px-12">
            <div className="grid gap-14 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <img
                        src={LOGO_FULL}
                        alt="Global Green Hotel — Sustainable Hospitality, Better Planet, Stronger Business"
                        className="w-56 max-w-full drop-shadow-[0_0_30px_rgba(194,161,96,0.2)]"
                        loading="lazy"
                    />
                    <p className="mt-6 max-w-sm text-sm leading-relaxed text-[#FDFBF7]/60">
                        {ORG.tagline}. A hospitality sustainability initiative helping hotels
                        measure, improve and communicate their sustainability performance.
                    </p>
                    <div className="mt-7 flex gap-3">
                        {SOCIALS.map(({ icon: Icon, label, href }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`social-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                aria-label={`Global Green Hotel on ${label}`}
                                title={`Global Green Hotel on ${label}`}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#FDFBF7]/70 transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#C2A160] hover:text-[#C2A160]"
                            >
                                <Icon size={17} aria-hidden="true" />
                            </a>
                        ))}
                    </div>
                </div>

                <nav className="lg:col-span-3" aria-label="Footer navigation">
                    <h3 className="eyebrow-gold">Explore</h3>
                    <ul className="mt-5 space-y-3">
                        {QUICK_LINKS.map((link) => (
                            <li key={link.to}>
                                <Link
                                    to={link.to}
                                    data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="text-sm text-[#FDFBF7]/70 transition-colors hover:text-[#D9B96A]"
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="lg:col-span-4">
                    <h3 className="eyebrow-gold">Contact</h3>
                    <ul className="mt-5 space-y-4 text-sm text-[#FDFBF7]/70">
                        <li className="flex gap-3">
                            <MapPin size={16} className="mt-0.5 shrink-0 text-[#C2A160]" aria-hidden="true" />
                            <span data-testid="footer-address">{ORG.location}</span>
                        </li>
                        <li>
                            <a
                                href={`mailto:${ORG.email}`}
                                data-testid="footer-email"
                                className="flex items-center gap-3 transition-colors hover:text-[#D9B96A]"
                            >
                                <Mail size={16} className="shrink-0 text-[#C2A160]" aria-hidden="true" />
                                {ORG.email}
                            </a>
                        </li>
                        <li>
                            <a
                                href={ORG.phoneHref}
                                data-testid="footer-phone"
                                className="flex items-center gap-3 transition-colors hover:text-[#D9B96A]"
                            >
                                <Phone size={16} className="shrink-0 text-[#C2A160]" aria-hidden="true" />
                                {ORG.phone}
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-7 sm:flex-row sm:items-center">
                <p className="text-xs text-[#FDFBF7]/50" data-testid="footer-copyright">
                    © 2026 Global Green Hotel. All rights reserved.
                </p>
                <div className="flex flex-wrap gap-6 text-xs">
                    <Link to="/privacy" data-testid="footer-link-privacy" className="text-[#FDFBF7]/50 transition-colors hover:text-[#D9B96A]">
                        Privacy Policy
                    </Link>
                    <Link to="/terms" data-testid="footer-link-terms" className="text-[#FDFBF7]/50 transition-colors hover:text-[#D9B96A]">
                        Terms of Use
                    </Link>
                    <Link to="/accessibility" data-testid="footer-link-accessibility" className="text-[#FDFBF7]/50 transition-colors hover:text-[#D9B96A]">
                        Accessibility
                    </Link>
                </div>
            </div>
        </div>
    </footer>
);
